'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog (rendered as the Radix trigger). */
  trigger?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Content width in px. */
  width?: number;
  className?: string;
  /**
   * Portal target. Defaults to `document.body`. Pass an element to render the overlay + content
   * inside a specific subtree — required when the surrounding theme (`[data-theme]`/`[data-mode]`)
   * or styles live on a wrapper (not `:root`), e.g. inside a themed panel or a Shadow DOM root.
   */
  container?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>['container'];
}

/** Centered modal. Escape + backdrop close; springy pop-in. */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  subtitle,
  children,
  footer,
  width = 460,
  className,
  container,
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal container={container}>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[var(--z-overlay)] bg-[color-mix(in_srgb,var(--color-text-primary)_45%,transparent)] animate-[lb-fade_var(--duration-fast)_var(--ease-standard)]"
        />
        <DialogPrimitive.Content
          style={{ width }}
          className={cn(
            'fixed left-1/2 top-1/2 z-[var(--z-modal)] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2',
            'overflow-hidden rounded-card bg-surface shadow-xl outline-none',
            'animate-[lb-pop_var(--duration-base)_var(--ease-spring)]',
            className,
          )}
        >
          {title ? (
            <div className="px-5 pb-3 pt-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <DialogPrimitive.Title className="text-xl font-bold tracking-[-0.01em] text-fg">
                    {title}
                  </DialogPrimitive.Title>
                  {subtitle ? (
                    <DialogPrimitive.Description className="mt-1 text-sm text-fg-secondary">
                      {subtitle}
                    </DialogPrimitive.Description>
                  ) : null}
                </div>
                <DialogPrimitive.Close
                  aria-label="닫기"
                  className="cursor-pointer p-1 text-fg-muted hover:text-fg"
                >
                  <X className="size-5" />
                </DialogPrimitive.Close>
              </div>
            </div>
          ) : null}
          <div className={title ? 'px-5 pb-5' : 'p-5'}>{children}</div>
          {footer ? (
            <div className="flex justify-end gap-2 border-t border-border bg-surface-2 px-5 py-4">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export const DialogClose = DialogPrimitive.Close;
